/**
 * Analytics service for fetching dashboard data and metrics.
 */

import { apiClient } from './apiClient';
import { logError } from '../utils/errorLogger';

export interface AdminDashboardData {
  system_overview: {
    total_events: number;
    active_events: number;
    pending_approvals: number;
    total_guests: number;
    total_invitations: number;
    accepted_invitations: number;
  };
  recent_activity: {
    recent_events: number;
    recent_invitations: number;
    recent_responses: number;
  };
  high_no_show_events: Array<{
    id: string;
    title: string;
    host: string;
    event_date: string;
    total_invited: number;
    accepted_count: number;
    acceptance_rate: number;
  }>;
  category_performance: Array<{
    category__name: string;
    event_count: number;
    avg_acceptance_rate: number;
  }>;
  host_performance: Array<{
    id: string;
    name: string;
    events_created: number;
    total_guests: number;
    avg_acceptance_rate: number;
  }>;
}

export interface HostDashboardData {
  overview: {
    total_events: number;
    active_events: number;
    total_invitations: number;
    accepted_invitations: number;
    rejected_invitations: number;
    pending_invitations: number;
  };
  metrics: {
    response_rate: number;
    acceptance_rate: number;
    companion_usage_rate: number;
    repeat_guests: number;
  };
  recent_events: Array<{
    id: string;
    title: string;
    event_date: string;
    total_invited: number;
    accepted_count: number;
    response_rate: number;
    expected_attendance_percentage?: number | null;
    series_id?: string | null;
    series_name?: string | null;
    is_main_event?: boolean;
  }>;
  categories: Array<{
    category__name: string;
    event_count: number;
    avg_guests: number;
  }>;
}

export interface GuestDashboardData {
  upcoming_events: Array<{
    id: string;
    event_id: string;
    title: string;
    event_date: string;
    event_time: string;
    location: string;
    status: string;
    companion_count: number;
    companions: Array<{
      id: string;
      guest_event_id: string;
      name: string;
      phone: string;
      status: string;
      is_companion: boolean;
    }>;
    can_edit_response: boolean;
  }>;
  past_events: Array<{
    id: string;
    event_id: string;
    title: string;
    event_date: string;
    status: string;
    companion_count: number;
    companions: Array<{
      id: string;
      guest_event_id: string;
      name: string;
      phone: string;
      status: string;
      is_companion: boolean;
    }>;
  }>;
  statistics: {
    total_invited: number;
    accepted_events: number;
    rejected_events: number;
    pending_events: number;
    companions_brought: number;
    avg_response_time_hours: number;
  };
}

export interface AnalyticsFilters {
  start_date?: string;
  end_date?: string;
  category_id?: number;
  host_id?: string;
}

export interface ExportRequest {
  format: 'csv' | 'excel';
  start_date?: string;
  end_date?: string;
  category_id?: number;
  host_id?: string;
  include_details?: boolean;
}

// Guest Behavior Tracking Types

export interface GuestBehaviorPattern {
  guest: string;
  guest_name: string;
  guest_phone: string;
  pattern_type: 'highly_responsive' | 'slow_responder' | 'non_responsive' | 
                'reliable_attender' | 'frequent_no_show' | 'consistent_decliner' | 
                'inconsistent' | 'insufficient_data';
  pattern_description: string;
  confidence_score: number;
  total_invitations: number;
  total_responses: number;
  total_accepted: number;
  total_attended: number;
  response_stats: Record<string, any>;
  attendance_stats: Record<string, any>;
  average_response_time_hours: number;
  response_rate: number;
  acceptance_rate: number;
  no_show_rate: number;
  created_at: string;
  updated_at: string;
  last_calculated?: string;
}

export interface GuestEventHistoryEntry {
  id: number;
  guest_event: number;
  guest_name: string;
  event_title: string;
  change_type: 'status_change' | 'companion_update' | 'attendance_update' | 
               'response_update' | 'field_update' | 'invitation_sent' | 'whatsapp_update' | 'created';
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_by: {
    id: string;
    email?: string;
    phone?: string;
    first_name: string;
    last_name: string;
    role?: string;
  } | null;
  changed_by_name: string;
  changed_at: string;
  metadata: Record<string, any>;
}

export interface EventGuestGroup {
  event_id: string;
  event_title: string;
  event_date: string;
  event_time?: string | null;
  location: string;
  host_name?: string;
  guest_count: number;
  guests: GuestBehaviorPattern[];
}

export interface GuestBehaviorAnalytics {
  total_guests_analyzed: number;
  total_invitations_sent: number;
  average_response_rate: number;
  average_no_show_rate: number;
  pattern_distribution: Record<string, number>;
  guest_patterns?: GuestBehaviorPattern[]; // Deprecated - kept for backward compatibility
  events: EventGuestGroup[];
  top_responsive_guests: GuestBehaviorPattern[];
  problematic_guests: GuestBehaviorPattern[];
  behavior_trends?: Array<Record<string, any>>;
}

export interface GuestBehaviorFilters {
  guest_id?: string;
  pattern_type?: string;
  start_date?: string;
  end_date?: string;
  search_query?: string;
  event_id?: string;
}

export interface GuestHistoryResponse {
  guest_id: string;
  total_records: number;
  history: GuestEventHistoryEntry[];
}

class AnalyticsService {
  /**
   * Get admin dashboard analytics data
   * @param filters Optional filters to apply to the analytics data
   */
  async getAdminDashboard(filters?: AnalyticsFilters): Promise<AdminDashboardData> {
    const params = new URLSearchParams();
    
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.category_id) params.append('category_id', filters.category_id.toString());
    if (filters?.host_id) params.append('host_id', filters.host_id);
    
    const queryString = params.toString();
    const url = `/analytics/dashboard/admin_dashboard/${queryString ? '?' + queryString : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Get host dashboard analytics data
   * @param filters Optional filters to apply to the analytics data
   */
  async getHostDashboard(filters?: AnalyticsFilters): Promise<HostDashboardData> {
    const params = new URLSearchParams();
    
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    const queryString = params.toString();
    const url = `/analytics/dashboard/host_dashboard/${queryString ? '?' + queryString : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Get guest dashboard analytics data
   */
  async getGuestDashboard(): Promise<GuestDashboardData> {
    const response = await apiClient.get('/analytics/dashboard/guest_dashboard/');
    return response.data;
  }

  /**
   * Get filtered analytics data
   */
  async getFilteredAnalytics(filters: AnalyticsFilters): Promise<any> {
    const params = new URLSearchParams();
    
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.category_id) params.append('category_id', filters.category_id.toString());
    if (filters.host_id) params.append('host_id', filters.host_id);
    
    const response = await apiClient.get(`/analytics/dashboard/filtered_analytics/?${params.toString()}`);
    return response.data;
  }

  /**
   * Create analytics snapshot
   */
  async createSnapshot(): Promise<any> {
    const response = await apiClient.post('/analytics/dashboard/create_snapshot/');
    return response.data;
  }

  /**
   * Update all analytics
   */
  async updateAllAnalytics(): Promise<void> {
    await apiClient.post('/analytics/dashboard/update_all_analytics/');
  }

  /**
   * Helper to parse blob error response
   */
  private async parseBlobError(blob: Blob): Promise<string> {
    try {
      const text = await blob.text();
      try {
        const errorData = JSON.parse(text);
        return errorData.error || errorData.message || errorData.detail || 'An error occurred';
      } catch {
        // If it's not JSON, return the text itself
        return text || 'An error occurred while processing the response';
      }
    } catch {
      return 'An error occurred while processing the response';
    }
  }

  /**
   * Export events analytics
   */
  async exportEventsAnalytics(exportRequest: ExportRequest): Promise<{ blob: Blob; contentType: string }> {
    try {
      const response = await apiClient.post('/analytics/export/export_events/', exportRequest, {
        responseType: 'blob',
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors, we'll handle them
      });
      
      // Check if response is an error (status 400-499)
      if (response.status >= 400 && response.status < 500) {
        // Check if content-type indicates JSON error response
        const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
        if (contentType.includes('application/json')) {
          // Try to parse the error message from the blob
          try {
            const errorText = await (response.data as Blob).text();
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.error || 'Failed to export events analytics');
          } catch (parseErr) {
            throw new Error('Failed to export events analytics. Please check your input and try again.');
          }
        } else {
          // Not JSON, use generic error message
          throw new Error('Failed to export events analytics. Please check your input and try again.');
        }
      }
      
      // Extract content-type from response headers (axios normalizes to lowercase)
      const contentType = response.headers['content-type'] || 
                          response.headers['Content-Type'] ||
        (exportRequest.format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv');
      
      // Create a new blob with the correct MIME type
      const blob = new Blob([response.data], { type: contentType });
      
      return { blob, contentType };
    } catch (error: any) {
      logError('Export error caught', error);
      
      // If it's already an Error with a message, re-throw it
      if (error instanceof Error) {
        throw error;
      }
      
      // If it's a blob error response, try to parse it as JSON to get error message
      if (error.response?.data instanceof Blob) {
        try {
          const errorMessage = await this.parseBlobError(error.response.data);
          throw new Error(errorMessage);
        } catch (parseErr) {
          logError('Failed to parse blob error', parseErr);
          throw new Error('Failed to export events analytics. Please check the server logs for details.');
        }
      }
      // Re-throw with a more user-friendly message if possible
      const errorMessage = error.response?.data?.error || error.message || 'Failed to export events analytics';
      throw new Error(errorMessage);
    }
  }

  /**
   * Export guests analytics
   */
  async exportGuestsAnalytics(exportRequest: ExportRequest): Promise<{ blob: Blob; contentType: string }> {
    try {
      const response = await apiClient.post('/analytics/export/export_guests/', exportRequest, {
        responseType: 'blob',
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors, we'll handle them
      });
      
      // Check if response is an error (status 400-499)
      if (response.status >= 400 && response.status < 500) {
        // Check if content-type indicates JSON error response
        const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
        if (contentType.includes('application/json')) {
          // Try to parse the error message from the blob
          try {
            const errorText = await (response.data as Blob).text();
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.error || 'Failed to export guests analytics');
          } catch (parseErr) {
            throw new Error('Failed to export guests analytics. Please check your input and try again.');
          }
        } else {
          // Not JSON, use generic error message
          throw new Error('Failed to export guests analytics. Please check your input and try again.');
        }
      }
      
      // Extract content-type from response headers (axios normalizes to lowercase)
      const contentType = response.headers['content-type'] || 
                          response.headers['Content-Type'] ||
        (exportRequest.format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv');
      
      // Create a new blob with the correct MIME type
      const blob = new Blob([response.data], { type: contentType });
      
      return { blob, contentType };
    } catch (error: any) {
      logError('Export error caught', error);
      
      // If it's already an Error with a message, re-throw it
      if (error instanceof Error) {
        throw error;
      }
      
      // If it's a blob error response, try to parse it as JSON to get error message
      if (error.response?.data instanceof Blob) {
        try {
          const errorMessage = await this.parseBlobError(error.response.data);
          throw new Error(errorMessage);
        } catch (parseErr) {
          logError('Failed to parse blob error', parseErr);
          throw new Error('Failed to export guests analytics. Please check the server logs for details.');
        }
      }
      // Re-throw with a more user-friendly message if possible
      const errorMessage = error.response?.data?.error || error.message || 'Failed to export guests analytics';
      throw new Error(errorMessage);
    }
  }

  /**
   * Download file from blob
   */
  downloadFile(blob: Blob, filename: string, contentType?: string): void {
    // Ensure filename has correct extension based on content type
    if (contentType) {
      if (contentType.includes('spreadsheetml') || contentType.includes('excel')) {
        if (!filename.endsWith('.xlsx')) {
          filename = filename.replace(/\.(csv|excel)$/i, '') + '.xlsx';
        }
      } else if (contentType.includes('csv') || contentType.includes('text/csv')) {
        if (!filename.endsWith('.csv')) {
          filename = filename.replace(/\.(xlsx|excel)$/i, '') + '.csv';
        }
      }
    }
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get guest behavior analytics
   */
  async getGuestBehaviorAnalytics(filters?: GuestBehaviorFilters): Promise<GuestBehaviorAnalytics> {
    const params = new URLSearchParams();
    
    if (filters?.guest_id) params.append('guest_id', filters.guest_id);
    if (filters?.pattern_type) params.append('pattern_type', filters.pattern_type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.search_query) params.append('search_query', filters.search_query);
    if (filters?.event_id) params.append('event_id', filters.event_id);
    
    const url = `/analytics/dashboard/guest_behavior_analytics/?${params.toString()}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Get guest change history
   */
  async getGuestHistory(guestId: string, eventId?: string): Promise<GuestHistoryResponse> {
    const params = new URLSearchParams();
    params.append('guest_id', guestId);
    
    if (eventId) params.append('event_id', eventId);
    
    const response = await apiClient.get(`/analytics/dashboard/guest_history/?${params.toString()}`);
    return response.data;
  }
}

export const analyticsService = new AnalyticsService();
